import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog, Field, FIELD_CLASS } from '@/components/Dialog'
import { Icon } from '@/components/Icon'
import { fetchClients } from '@/features/clients/api'
import {
  createContract,
  createSlaPolicy,
  deleteContract,
  deleteSlaPolicy,
  updateContract,
  updateSlaPolicy,
} from '@/features/support/api'
import type { ApiError } from '@/lib/api/client'
import type { Client, Contract, ContractStatus, SlaPolicy } from '@/types/support'

const STATUSES: ContractStatus[] = ['draft', 'active', 'expired', 'terminated']
const PRIORITIES = ['urgent', 'high', 'normal', 'low']

/**
 * A contract and the service levels under it.
 *
 * These decide when a client is owed a response, and until now they could only
 * be seeded — which meant the promise the support desk measures against was not
 * something anyone at AWJ could state, change or even read in the product.
 *
 * The client is fixed once the contract exists: a contract that changes hands
 * is a new contract, and the server refuses the field on update rather than
 * quietly reassigning history.
 */
export function ContractDialog({
  contract,
  onClose,
}: {
  contract?: Contract
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const creating = contract === undefined

  const [form, setForm] = useState({
    clientId: contract?.clientId ?? '',
    name: contract?.name ?? '',
    reference: contract?.reference ?? '',
    startsOn: contract?.startsOn?.slice(0, 10) ?? '',
    endsOn: contract?.endsOn?.slice(0, 10) ?? '',
    status: (contract?.status ?? 'draft') as ContractStatus,
    notes: contract?.notes ?? '',
  })

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))

  const clients = useQuery<Client[], ApiError>({
    queryKey: ['clients'],
    queryFn: fetchClients,
    retry: false,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['contracts'] })
  const blank = (v: string): string | null => (v.trim() === '' ? null : v.trim())

  const save = useMutation<Contract, ApiError, void>({
    mutationFn: () =>
      creating
        ? createContract({
            clientId: form.clientId,
            name: form.name.trim(),
            reference: blank(form.reference),
            startsOn: form.startsOn,
            endsOn: blank(form.endsOn),
            status: form.status,
            notes: blank(form.notes),
          })
        : updateContract(contract.id, {
            name: form.name.trim(),
            reference: blank(form.reference),
            startsOn: form.startsOn,
            endsOn: blank(form.endsOn),
            status: form.status,
            notes: blank(form.notes),
          }),
    onSuccess: () => {
      void refresh()
      onClose()
    },
  })

  const remove = useMutation<void, ApiError, void>({
    mutationFn: () => deleteContract(contract?.id ?? ''),
    onSuccess: () => {
      void refresh()
      onClose()
    },
  })

  const ready = form.name.trim() !== '' && form.startsOn !== '' && (!creating || form.clientId !== '')

  return (
    <Dialog title={creating ? t('support.newContract') : contract.name} onClose={onClose}>
      <div className="flex max-h-[72vh] flex-col gap-3.5 overflow-y-auto px-5 py-4">
        <form
          className="flex flex-col gap-3.5"
          onSubmit={(event) => {
            event.preventDefault()
            if (ready) save.mutate()
          }}
        >
          {creating ? (
            <Field label={t('support.client')}>
              <select
                value={form.clientId}
                autoFocus
                onChange={(event) => set('clientId')(event.target.value)}
                className={FIELD_CLASS}
              >
                <option value="">—</option>
                {(clients.data ?? []).map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </Field>
          ) : (
            <p className="text-ink-500 text-[12px]">{contract.client?.name ?? '—'}</p>
          )}

          <Field label={t('support.contractName')}>
            <input value={form.name} onChange={(e) => set('name')(e.target.value)} className={FIELD_CLASS} />
          </Field>

          <div className="grid grid-cols-2 gap-3.5">
            <Field label={t('support.reference')}>
              <input value={form.reference} onChange={(e) => set('reference')(e.target.value)} className={FIELD_CLASS} />
            </Field>
            <Field label={t('support.contractStatus')}>
              <select value={form.status} onChange={(e) => set('status')(e.target.value)} className={FIELD_CLASS}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`support.status${s.charAt(0).toUpperCase()}${s.slice(1)}`)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('support.startsOn')}>
              <input type="date" value={form.startsOn} onChange={(e) => set('startsOn')(e.target.value)} className={FIELD_CLASS} />
            </Field>
            <Field label={t('support.endsOn')}>
              <input type="date" value={form.endsOn} onChange={(e) => set('endsOn')(e.target.value)} className={FIELD_CLASS} />
            </Field>
          </div>

          <Field label={t('support.notes')}>
            <textarea value={form.notes} rows={2} onChange={(e) => set('notes')(e.target.value)} className={`${FIELD_CLASS} resize-none`} />
          </Field>

          {save.isError ? (
            <p className="text-[12px] text-[color:var(--color-state-blocked)]">{save.error.message}</p>
          ) : null}

          <div className="flex items-center gap-3">
            {creating ? null : (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('support.deleteContractConfirm'))) remove.mutate()
                }}
                className="text-ink-500 hover:text-[color:var(--color-state-blocked)] text-[12px] underline"
              >
                {t('support.deleteContract')}
              </button>
            )}
            <button
              type="submit"
              disabled={!ready || save.isPending}
              className="bg-brand-600 ms-auto rounded px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-40"
            >
              {t('support.saveContract')}
            </button>
          </div>
        </form>

        {creating ? null : <SlaPolicies contract={contract} onChanged={() => void refresh()} />}
      </div>
    </Dialog>
  )
}

/** The tiers under one contract. Minutes are working minutes; the note says so. */
function SlaPolicies({ contract, onChanged }: { contract: Contract; onChanged: () => void }) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)

  return (
    <section className="border-line border-t pt-3.5">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
          {t('support.slaPolicies')}
        </h3>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="text-brand-600 ms-auto flex items-center gap-1 text-[11px]"
        >
          <Icon name="plus" className="size-3" />
          {t('support.addSla')}
        </button>
      </div>

      <ul className="flex flex-col gap-1.5">
        {contract.slaPolicies.map((policy) => (
          <PolicyRow key={policy.id} policy={policy} onChanged={onChanged} />
        ))}
        {adding ? (
          <PolicyRow
            contractId={contract.id}
            onChanged={() => {
              setAdding(false)
              onChanged()
            }}
            onCancel={() => setAdding(false)}
          />
        ) : null}
      </ul>

      <p className="text-ink-500 mt-2 text-[11px] leading-tight">{t('support.minutesNote')}</p>
    </section>
  )
}

function PolicyRow({
  policy,
  contractId,
  onChanged,
  onCancel,
}: {
  policy?: SlaPolicy
  contractId?: string
  onChanged: () => void
  onCancel?: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    name: policy?.name ?? '',
    priority: policy?.priority ?? 'normal',
    firstResponseMinutes: String(policy?.firstResponseMinutes ?? 240),
    resolutionMinutes: String(policy?.resolutionMinutes ?? 2400),
    businessHoursOnly: policy?.businessHoursOnly ?? true,
  })

  const set = (key: keyof typeof form) => (value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }))

  const save = useMutation<SlaPolicy, ApiError, void>({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        priority: form.priority,
        firstResponseMinutes: Number(form.firstResponseMinutes),
        resolutionMinutes: Number(form.resolutionMinutes),
        businessHoursOnly: form.businessHoursOnly,
      }

      return policy === undefined
        ? createSlaPolicy({ ...payload, contractId: contractId ?? null })
        : updateSlaPolicy(policy.id, payload)
    },
    onSuccess: onChanged,
  })

  const remove = useMutation<void, ApiError, void>({
    mutationFn: () => deleteSlaPolicy(policy?.id ?? ''),
    onSuccess: onChanged,
  })

  return (
    <li className="border-line flex flex-wrap items-end gap-1.5 rounded border p-2">
      <label className="flex min-w-28 flex-1 flex-col gap-0.5">
        <span className="text-ink-500 font-mono text-[9px] tracking-widest uppercase">{t('support.slaName')}</span>
        <input value={form.name} onChange={(e) => set('name')(e.target.value)} className={`${FIELD_CLASS} py-1 text-[12px]`} />
      </label>

      <label className="flex w-24 flex-col gap-0.5">
        <span className="text-ink-500 font-mono text-[9px] tracking-widest uppercase">{t('support.priority')}</span>
        <select value={form.priority} onChange={(e) => set('priority')(e.target.value)} className={`${FIELD_CLASS} py-1 text-[12px]`}>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>{t(`priority.${p}`)}</option>
          ))}
        </select>
      </label>

      <label className="flex w-20 flex-col gap-0.5">
        <span className="text-ink-500 font-mono text-[9px] tracking-widest uppercase">{t('support.firstResponse')}</span>
        <input inputMode="numeric" value={form.firstResponseMinutes} onChange={(e) => set('firstResponseMinutes')(e.target.value)} className={`${FIELD_CLASS} py-1 font-mono text-[12px] tabular-nums`} />
      </label>

      <label className="flex w-20 flex-col gap-0.5">
        <span className="text-ink-500 font-mono text-[9px] tracking-widest uppercase">{t('support.resolution')}</span>
        <input inputMode="numeric" value={form.resolutionMinutes} onChange={(e) => set('resolutionMinutes')(e.target.value)} className={`${FIELD_CLASS} py-1 font-mono text-[12px] tabular-nums`} />
      </label>

      <label className="flex items-center gap-1 pb-1.5 text-[11px]">
        <input type="checkbox" checked={form.businessHoursOnly} onChange={(e) => set('businessHoursOnly')(e.target.checked)} />
        <span className="text-ink-500">{t('support.businessHoursOnly')}</span>
      </label>

      <button
        type="button"
        onClick={() => save.mutate()}
        disabled={save.isPending || form.name.trim() === ''}
        className="bg-brand-600 rounded px-2 py-1 text-[11px] text-white disabled:opacity-40"
      >
        {t('support.saveSla')}
      </button>

      {policy === undefined ? (
        <button type="button" onClick={onCancel} className="text-ink-500 px-1.5 py-1 text-[11px]">
          {t('document.cancel')}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => remove.mutate()}
          title={t('support.deleteSla')}
          aria-label={t('support.deleteSla')}
          className="text-ink-500 hover:text-[color:var(--color-state-blocked)] rounded p-1"
        >
          <Icon name="trash" className="size-3" />
        </button>
      )}

      {save.isError ? (
        <span className="w-full text-[11px] text-[color:var(--color-state-blocked)]">{save.error.message}</span>
      ) : null}
    </li>
  )
}
